import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import { formatMoney } from '@/lib/format';
import type { Product, ProductDiscount } from '@/lib/types';

interface DiscountWithProduct extends ProductDiscount {
  product: { name: string; sale_price: number } | null;
}

interface FormState {
  product_id: string;
  product_name: string;
  percentage: number;
  starts_at: string;
  ends_at: string;
  active: boolean;
}

const emptyForm: FormState = {
  product_id: '',
  product_name: '',
  percentage: 0,
  starts_at: '',
  ends_at: '',
  active: true,
};

const toDateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : '');

export default function ProductDiscounts() {
  const [items, setItems] = useState<DiscountWithProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProductDiscount | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  // Búsqueda de producto
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('product_discounts')
        .select('*, product:products(name, sale_price)')
        .order('created_at', { ascending: false });
      if (e) throw e;
      setItems((data as DiscountWithProduct[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los descuentos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const term = search.trim();
      if (!term) {
        setResults([]);
        return;
      }
      setSearching(true);
      const { data } = await supabase
        .from('products')
        .select('*')
        .ilike('name', `%${term}%`)
        .eq('active', true)
        .limit(8);
      setResults((data as Product[]) ?? []);
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setSearch('');
    setResults([]);
    setModalOpen(true);
  };

  const openEdit = (d: DiscountWithProduct) => {
    setEditing(d);
    setForm({
      product_id: d.product_id,
      product_name: d.product?.name ?? '',
      percentage: Number(d.percentage),
      starts_at: toDateInput(d.starts_at),
      ends_at: toDateInput(d.ends_at),
      active: d.active,
    });
    setSearch('');
    setResults([]);
    setModalOpen(true);
  };

  const pickProduct = (p: Product) => {
    setForm((prev) => ({ ...prev, product_id: p.id, product_name: p.name }));
    setSearch('');
    setResults([]);
  };

  const handleSave = async () => {
    if (!form.product_id) {
      setError('Selecciona un producto.');
      return;
    }
    if (form.percentage < 0 || form.percentage > 100) {
      setError('El porcentaje debe estar entre 0 y 100.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        product_id: form.product_id,
        percentage: form.percentage,
        starts_at: form.starts_at ? `${form.starts_at}T00:00:00` : null,
        ends_at: form.ends_at ? `${form.ends_at}T23:59:59` : null,
        active: form.active,
      };
      if (editing) {
        const { error: e } = await supabase
          .from('product_discounts')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', editing.id);
        if (e) throw e;
      } else {
        const { error: e } = await supabase
          .from('product_discounts')
          .upsert(payload, { onConflict: 'product_id' });
        if (e) throw e;
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el descuento');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (d: DiscountWithProduct) => {
    const { error: e } = await supabase
      .from('product_discounts')
      .update({ active: !d.active, updated_at: new Date().toISOString() })
      .eq('id', d.id);
    if (e) return;
    load();
  };

  const remove = async (d: DiscountWithProduct) => {
    const { error: e } = await supabase.from('product_discounts').delete().eq('id', d.id);
    if (e) return;
    load();
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-600">
          {items.length} {items.length === 1 ? 'descuento' : 'descuentos'} configurados. El precio
          normal del producto nunca se modifica.
        </p>
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-add-line" /> Nuevo descuento
        </button>
      </div>

      {error && !modalOpen && (
        <div className="mb-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-percent-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Aún no hay descuentos por producto.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-background-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Producto</th>
                <th className="px-4 py-3 font-medium">Precio normal</th>
                <th className="px-4 py-3 font-medium">Descuento</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Precio final</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Vigencia</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {items.map((d) => {
                const base = d.product?.sale_price ?? 0;
                const final = Math.round(base * (100 - Number(d.percentage))) / 100;
                return (
                  <tr key={d.id} className="hover:bg-background-50">
                    <td className="px-4 py-3 font-medium text-foreground-950">
                      {d.product?.name ?? 'Producto eliminado'}
                    </td>
                    <td className="px-4 py-3 text-foreground-700">{formatMoney(base, 'RD$')}</td>
                    <td className="px-4 py-3 text-accent-700 font-semibold">-{Number(d.percentage)}%</td>
                    <td className="px-4 py-3 text-foreground-700 hidden md:table-cell">
                      {formatMoney(final, 'RD$')}
                    </td>
                    <td className="px-4 py-3 text-foreground-500 hidden lg:table-cell">
                      {d.starts_at || d.ends_at
                        ? `${d.starts_at ? new Date(d.starts_at).toLocaleDateString('es-DO') : '—'} → ${
                            d.ends_at ? new Date(d.ends_at).toLocaleDateString('es-DO') : '—'
                          }`
                        : 'Sin límite'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          d.active ? 'bg-accent-100 text-accent-900' : 'bg-secondary-100 text-secondary-900'
                        }`}
                      >
                        {d.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => toggle(d)}
                          className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                          title={d.active ? 'Desactivar' : 'Activar'}
                        >
                          <i className={d.active ? 'ri-pause-circle-line' : 'ri-play-circle-line'} />
                        </button>
                        <button
                          onClick={() => openEdit(d)}
                          className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                          title="Editar"
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          onClick={() => remove(d)}
                          className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-accent-50 hover:text-accent-700 cursor-pointer"
                          title="Eliminar"
                        >
                          <i className="ri-delete-bin-6-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar descuento' : 'Nuevo descuento de producto'}
        footer={
          <>
            <button
              onClick={() => setModalOpen(false)}
              disabled={saving}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Producto *</label>
            {editing ? (
              <div className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-900">
                {form.product_name}
              </div>
            ) : (
              <>
                <input
                  value={form.product_id ? form.product_name : search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar producto por nombre..."
                  className={inputClass}
                />
                {searching && (
                  <p className="mt-1.5 text-xs text-foreground-500">Buscando...</p>
                )}
                {results.length > 0 && (
                  <div className="mt-1.5 rounded-md border border-background-200 divide-y divide-background-200 max-h-48 overflow-y-auto">
                    {results.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => pickProduct(p)}
                        className="w-full text-left px-3 py-2 hover:bg-background-100 text-sm cursor-pointer"
                      >
                        <span className="font-medium text-foreground-950">{p.name}</span>
                        <span className="ml-2 text-foreground-500">
                          {formatMoney(p.sale_price, 'RD$')}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className={labelClass}>Porcentaje de descuento (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.percentage}
              onChange={(e) => setForm((p) => ({ ...p, percentage: parseFloat(e.target.value) || 0 }))}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Fecha de inicio</label>
              <input
                type="date"
                value={form.starts_at}
                onChange={(e) => setForm((p) => ({ ...p, starts_at: e.target.value }))}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Fecha de vencimiento</label>
              <input
                type="date"
                value={form.ends_at}
                onChange={(e) => setForm((p) => ({ ...p, ends_at: e.target.value }))}
                className={inputClass}
              />
            </div>
          </div>

          <label className="flex items-center justify-between gap-3 rounded-md border border-background-200 p-3 cursor-pointer">
            <span className="text-sm font-medium text-foreground-900">Descuento activo</span>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
              className="w-5 h-5 accent-primary-500 cursor-pointer"
            />
          </label>

          {error && (
            <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
              {error}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}